/**
 * Fleet Vehicle Management API
 *
 * Fleet-portal (CLIENT user) endpoints for the fleet customer's own vehicles.
 *
 * SCOPING: none of these calls send a customer id. The backend resolves the
 * fleet customer from the `customerUuid` JWT claim and scopes every read and
 * write to it — a fleet user can only ever see and touch their own vehicles.
 */

import axiosInstance from './axiosInstance';

// ============================================
// List
// ============================================

/**
 * List this fleet customer's vehicles, paginated with an optional rego filter.
 *
 * @param {{ page?: number, size?: number, rego?: string }} params
 * @returns {Promise<{ content: Array, page: number, size: number, totalElements: number, totalPages: number }>}
 *   Each row: { uuid, rego, regoState, make, model, year, displayName }
 */
export const listFleetVehiclesApi = async ({ page = 0, size = 20, rego = '' } = {}) => {
    const params = { page, size };
    if (rego && rego.trim()) {
        params.rego = rego.trim();
    }
    const response = await axiosInstance.get('/fleet/vehicles', { params });
    return response.data;
};

// ============================================
// Create
// ============================================

/**
 * Add a vehicle to this fleet customer's fleet.
 *
 * Minimum fields needed to book a service: make, model, year, rego, regoState.
 *
 * @param {{ make: string, model: string, year: number, rego: string, regoState: string }} payload
 * @returns {Promise<Object>} the created vehicle row
 */
export const createFleetVehicleApi = async (payload) => {
    const response = await axiosInstance.post('/fleet/vehicles', payload);
    return response.data;
};

// ============================================
// Delete
// ============================================

/**
 * Remove a vehicle from this fleet customer's fleet.
 *
 * The backend soft-removes vehicles that have service history (bookings,
 * invoices or service records) and hard-deletes only those with none, so
 * history is never destroyed.
 */
export const deleteFleetVehicleApi = async (vehicleUuid) => {
    const response = await axiosInstance.delete(`/fleet/vehicles/${vehicleUuid}`);
    return response.data;
};

// ============================================
// Aggregate export
// ============================================

const fleetVehicleMgtApi = {
    listFleetVehiclesApi,
    createFleetVehicleApi,
    deleteFleetVehicleApi,
};

export default fleetVehicleMgtApi;